import { DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ResponsiveDialog } from "@/components/ui/responsive-dialog";
import { Button } from "@akuna/ui";
import { Input } from "@akuna/ui";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { SegmentedTabs } from "@akuna/ui";
import { Switch } from "@akuna/ui";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { DateFieldPopover } from "@/components/ui/date-field-popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { petVaccinationSchema, type PetVaccinationInput } from "@/lib/validations/application";
import { useCreatePetApplication, useDeletePetApplication, useUpdatePetApplication } from "@/hooks/usePetApplications";
import { useApplications } from "@/hooks/useApplications";
import { useCurrentClinicId } from "@/hooks/useAppointments";
import { format } from "date-fns";
import { CalendarIcon, Plus, Trash2, X } from "lucide-react";
import { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { z } from "zod";
import { cn } from "@/lib/utils";
import { ApplicationAutocomplete } from "./ApplicationAutocomplete";
import { CreateApplicationDialog } from "./CreateApplicationDialog";
import { combineDateAndTime, isValidTimeHHmm, setDateOnlyNoon } from "@/lib/utils/next-dose";
import { convertDisplayToISO, formatISOToDisplay, getDateErrorMessage, maskDateInput } from "@/lib/date-utils";
import { useMaskedDigitsCaret } from "@/hooks/useMaskedDigitsCaret";
import type { VaccineOption } from "@/hooks/useApplications";
import { useTranslation } from "react-i18next";
import { calculateDoseIntervalDate, hasDoseInterval } from "@/lib/dose-interval";
import { buildApplicationDoseSuggestions } from "@/lib/application-dose-suggestions";
import { FiscalCombobox } from "@/components/erp/FiscalCombobox";
import { ApplicationPlanForm } from "./ApplicationPlanForm";
import { useCompletePetApplicationPlan } from "@/hooks/usePetApplicationPlans";
import type { UpcomingApplicationDialogContext } from "./upcoming-dialog-context";
import {
  makeNextApplicationEntry,
  buildEntriesFromDefaults,
  type NextApplicationEntry,
} from "./NextApplicationsList";
import type { PetApplicationFormDraft } from "@/pages/daycare-hotel/reservation/petApplicationDraft";
import {
  dispatchApplicationSubmission,
  type ApplicationSubmissionMode,
} from "./applicationSubmission";
import type { PetApplicationPlanFormDraft } from "@/pages/daycare-hotel/reservation/petApplicationPlanDraft";

interface ApplicationFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  petId: string;
  clinicId?: string;
  appointmentId?: string;
  medicalRecordId?: string;
  petSpeciesId?: string;
  vaccination?: ApplicationFormDialogVaccination | null;
  plan?: {
    id: string;
    item_id: string;
    due_date: string;
    notes?: string | null;
    appointment_id?: string | null;
    source_type?: "manual" | "derived_from_application" | null;
    source_pet_application_id?: string | null;
  } | null;
  showCreateAppointmentToggle?: boolean;
  initialTab?: "record" | "plan";
  vaccinationDeleteMode?: "delete_record" | "clear_next_dose_reminder";
  upcomingContext?: UpcomingApplicationDialogContext | null;
  onApplicationSaved?: (application: ApplicationFormDialogVaccination & { id?: string }) => void;
  onApplicationDeleted?: (applicationId: string) => void;
  onPlanSaved?: () => void;
  submissionMode?: ApplicationSubmissionMode;
  onDraftSubmit?: (draft: PetApplicationFormDraft) => void;
  onDraftDelete?: (applicationId: string) => void;
  onPlanDraftSubmit?: (draft: PetApplicationPlanFormDraft) => void;
  onPlanDraftDelete?: (planId: string) => void;
}

type ApplicationFormDialogVaccination = {
  id?: string;
  item_id?: string | { id?: string; name?: string | null } | null;
  next_application_item_id?: string | { id?: string; name?: string | null } | null;
  item?: { name?: string | null } | null;
  next_application_item?: { name?: string | null } | null;
  vaccine_item?: { name?: string | null } | null;
  vaccine_name?: string | null;
  application_date: string | Date;
  dose_number?: number | null;
  notes?: string | null;
  expiry_date?: string | null;
  next_dose_date?: string | null;
  has_next_dose?: boolean | null;
  next_dose_completed_at?: string | null;
  next_dose_completed_by_application_id?: string | null;
  clinic_id?: string | null;
  appointment_id?: string | null;
  appointment?: { starts_at?: string | null } | null;
  medical_record_id?: string | null;
};

const getApplicationItemIdFromValue = (
  value?: string | { id?: string; name?: string | null } | null,
) => {
  if (!value) return null;
  const id = typeof value === "object" ? value.id ?? null : value;
  const trimmed = id?.trim();
  return trimmed || null;
};

const petVaccinationSchemaWithAppointmentToggle = petVaccinationSchema
  .and(
    z.object({
      create_appointment: z.boolean().optional(),
      next_dose_time: z.string().optional(),
    }),
  )
  .superRefine((data, ctx) => {
    const typed = data as PetVaccinationInput & { create_appointment?: boolean; next_dose_time?: string };
    if (typed.create_appointment === true && typed.has_next_dose !== true) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["has_next_dose"],
        message: "vaccines.validation.nextDoseToggleRequired",
      });
    }
    if (typed.create_appointment === true && !typed.next_dose_date) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["next_dose_date"],
        message: "vaccines.validation.nextDoseDateRequired",
      });
    }
    if (typed.create_appointment === true && (!typed.next_dose_time || !isValidTimeHHmm(typed.next_dose_time))) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["next_dose_time"],
        message: "vaccines.validation.nextDoseTimeRequired",
      });
    }
  });

type PetVaccinationFormInput = PetVaccinationInput & {
  create_appointment?: boolean;
  next_dose_time?: string;
};

export function ApplicationFormDialog({
  open,
  onOpenChange,
  petId,
  clinicId: propClinicId,
  appointmentId: propAppointmentId,
  medicalRecordId: propMedicalRecordId,
  petSpeciesId,
  vaccination,
  plan,
  showCreateAppointmentToggle,
  initialTab = "record",
  vaccinationDeleteMode = "delete_record",
  upcomingContext,
  onApplicationSaved,
  onPlanSaved,
  onApplicationDeleted,
  submissionMode = "immediate",
  onDraftSubmit,
  onDraftDelete,
  onPlanDraftSubmit,
  onPlanDraftDelete,
}: ApplicationFormDialogProps) {
  const { t } = useTranslation("reports");
  const { data: currentClinicId } = useCurrentClinicId();
  const recordFormId = useId();
  const [footerEl, setFooterEl] = useState<HTMLDivElement | null>(null);
  const [activeTab, setActiveTab] = useState<"record" | "plan">("record");
  const [suggestedNextDoseDate, setSuggestedNextDoseDate] = useState<Date | null>(null);
  const [showCancelAppointmentDialog, setShowCancelAppointmentDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [pendingSubmitData, setPendingSubmitData] = useState<PetVaccinationFormInput | null>(null);
  const [appointmentConfirmReason, setAppointmentConfirmReason] = useState<
    "remove_next_dose_date" | "disable_create_appointment" | null
  >(null);
  const [vaccineSearch, setVaccineSearch] = useState("");
  const [createVaccineDialogOpen, setCreateVaccineDialogOpen] = useState(false);
  const [createdOption, setCreatedOption] = useState<VaccineOption | null>(null);
  const [nextDoseDateInput, setNextDoseDateInput] = useState("");
  const [extraDateInputs, setExtraDateInputs] = useState<Record<string, string>>({});
  const [isNextDoseCalendarOpen, setIsNextDoseCalendarOpen] = useState(false);
  const { inputRef: nextDoseDateInputRef, captureCaret: captureNextDoseDateCaret } = useMaskedDigitsCaret(nextDoseDateInput);
  const [expiryDateInput, setExpiryDateInput] = useState("");
  const [isExpiryDateCalendarOpen, setIsExpiryDateCalendarOpen] = useState(false);
  const { inputRef: expiryDateInputRef, captureCaret: captureExpiryDateCaret } = useMaskedDigitsCaret(expiryDateInput);
  const nextDoseManualRef = useRef(false);
  const [nextAppEntries, setNextAppEntries] = useState<NextApplicationEntry[]>([]);

  // Use current clinic from hook, prop, or vaccination
  const clinicId = propClinicId || currentClinicId || vaccination?.clinic_id || "";
  const isUpcomingContext = Boolean(upcomingContext);
  const recordReminder = upcomingContext?.vaccination ?? null;
  const resolvedPlan = useMemo(() => {
    if (upcomingContext?.plan) return upcomingContext.plan;
    if (upcomingContext?.vaccination) {
      const reminder = upcomingContext.vaccination;
      const expectedItemId =
        (typeof reminder.next_application_item_id === "string" ? reminder.next_application_item_id : reminder.next_application_item_id?.id)
        ?? (typeof reminder.item_id === "string" ? reminder.item_id : reminder.item_id?.id)
        ?? null;

      if (!expectedItemId || !reminder.next_dose_date) return null;

      return {
        id: "",
        item_id: expectedItemId,
        due_date: reminder.next_dose_date,
        notes: reminder.notes ?? null,
        appointment_id: reminder.appointment_id ?? null,
        source_type: "derived_from_application" as const,
        source_pet_application_id: reminder.id ?? null,
      };
    }
    return plan ?? null;
  }, [plan, upcomingContext]);

  const includedApplicationItemIds = useMemo(() => {
    const ids = [
      getApplicationItemIdFromValue(vaccination?.item_id),
      getApplicationItemIdFromValue(vaccination?.next_application_item_id),
      getApplicationItemIdFromValue(recordReminder?.item_id),
      getApplicationItemIdFromValue(recordReminder?.next_application_item_id),
      resolvedPlan?.item_id ?? null,
      upcomingContext?.expectedItemId ?? null,
    ];

    return [...new Set(ids.filter((id): id is string => Boolean(id)))];
  }, [
    recordReminder?.item_id,
    recordReminder?.next_application_item_id,
    resolvedPlan?.item_id,
    upcomingContext?.expectedItemId,
    vaccination?.item_id,
    vaccination?.next_application_item_id,
  ]);

  const { data: applications, isLoading: isApplicationsLoading } = useApplications({
    speciesId: petSpeciesId,
    isActive: true,
    clinicId,
    includeItemIds: includedApplicationItemIds,
  });

  const applicationOptions = useMemo(() => {
    const base = applications ?? [];
    if (!createdOption) return base;
    const withoutCreated = base.filter((option) => option.itemId !== createdOption.itemId);
    return [createdOption, ...withoutCreated];
  }, [applications, createdOption]);

  const createVaccination = useCreatePetApplication();
  const updateVaccination = useUpdatePetApplication();
  const deleteVaccination = useDeletePetApplication();
  const completePlan = useCompletePetApplicationPlan();

  const getVaccineItemId = useCallback(() => {
    if (!vaccination) return undefined;
    return getApplicationItemIdFromValue(vaccination.item_id) ?? undefined;
  }, [vaccination]);

  const getVaccinationDisplayName = useCallback(() => {
    if (!vaccination) return "";
    const vaccineItem = vaccination.item_id;
    return (
      vaccination.item?.name ||
      (typeof vaccineItem === "object" && vaccineItem !== null ? vaccineItem?.name : "") ||
      vaccination.vaccine_item?.name ||
      vaccination.vaccine_name ||
      ""
    );
  }, [vaccination]);

  const getNextApplicationItemId = useCallback(() => {
    if (!vaccination) return undefined;
    return getApplicationItemIdFromValue(vaccination.next_application_item_id) ?? undefined;
  }, [vaccination]);

  const getNextApplicationDisplayName = useCallback(() => {
    if (!vaccination) return "";
    const nextItem = vaccination.next_application_item_id;
    return (
      vaccination.next_application_item?.name ||
      (typeof nextItem === "object" && nextItem !== null ? nextItem?.name : "") ||
      ""
    );
  }, [vaccination]);

  const getNextDoseTimeDefault = useCallback(() => {
    if (!vaccination) return "";
    const startsAtRaw = vaccination.appointment?.starts_at ?? vaccination.next_dose_date ?? null;
    if (!startsAtRaw) return "";
    const startsAt = new Date(startsAtRaw);
    if (Number.isNaN(startsAt.getTime())) return "";
    return format(startsAt, "HH:mm");
  }, [vaccination]);

  // Get appointment_id - from vaccination or prop
  const appointmentId = vaccination?.appointment_id || propAppointmentId;
  const vaccinationAppointmentId: string | null = vaccination?.appointment_id ?? null;

  const shouldShowCreateAppointmentToggle =
    submissionMode === "immediate" && Boolean(showCreateAppointmentToggle);

  const defaultValues = useMemo(
    () =>
      vaccination
        ? {
          ...vaccination,
          item_id: getVaccineItemId(),
          next_application_item_id: getNextApplicationItemId(),
          application_date: new Date(vaccination.application_date),
          expiry_date: vaccination.expiry_date ? new Date(vaccination.expiry_date) : undefined,
          next_dose_date: vaccination.next_dose_date ? new Date(vaccination.next_dose_date) : undefined,
          has_next_dose: Boolean(vaccination.has_next_dose ?? vaccination.next_dose_date),
          next_dose_time: shouldShowCreateAppointmentToggle && vaccinationAppointmentId ? getNextDoseTimeDefault() : "",
          clinic_id: vaccination.clinic_id || clinicId,
          appointment_id: vaccination.appointment_id || undefined,
          create_appointment: shouldShowCreateAppointmentToggle ? Boolean(vaccinationAppointmentId) : undefined,
        }
        : {
          pet_id: petId,
          clinic_id: clinicId,
          item_id: null,
          next_application_item_id: null,
          appointment_id: propAppointmentId || undefined,
          application_date: new Date(),
          dose_number: 1,
          has_next_dose: false,
          next_dose_date: undefined,
          next_dose_time: "",
          create_appointment: shouldShowCreateAppointmentToggle ? false : undefined,
          batch_number: undefined,
          expiry_date: undefined,
          adverse_reaction: undefined,
          notes: undefined,
        },
    [
      clinicId,
      getNextDoseTimeDefault,
      getNextApplicationItemId,
      getVaccineItemId,
      petId,
      propAppointmentId,
      shouldShowCreateAppointmentToggle,
      vaccination,
      vaccinationAppointmentId,
    ],
  );
  const recordDefaultValues = useMemo(() => {
    if (!isUpcomingContext) return defaultValues;

    return {
      pet_id: upcomingContext?.petId ?? petId,
      clinic_id: clinicId,
      item_id: upcomingContext?.expectedItemId ?? null,
      next_application_item_id: upcomingContext?.expectedItemId ?? null,
      appointment_id: undefined,
      application_date: new Date(),
      dose_number: typeof recordReminder?.dose_number === "number" ? recordReminder.dose_number : 1,
      has_next_dose: false,
      next_dose_date: undefined,
      next_dose_time: "",
      create_appointment: shouldShowCreateAppointmentToggle ? false : undefined,
      batch_number: undefined,
      expiry_date: undefined,
      adverse_reaction: undefined,
      notes: recordReminder?.notes ?? resolvedPlan?.notes ?? undefined,
    };
  }, [
    clinicId,
    defaultValues,
    isUpcomingContext,
    petId,
    recordReminder?.dose_number,
    recordReminder?.notes,
    resolvedPlan?.notes,
    shouldShowCreateAppointmentToggle,
    upcomingContext?.expectedItemId,
    upcomingContext?.petId,
  ]);

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    control,
    reset,
    getValues,
    formState: { errors },
  } = useForm<PetVaccinationFormInput>({
    resolver: zodResolver(
      shouldShowCreateAppointmentToggle ? petVaccinationSchemaWithAppointmentToggle : petVaccinationSchema,
    ),
    defaultValues: {
      ...recordDefaultValues,
      clinic_id: clinicId, // Always use current clinic
    },
  });

  // Update clinic_id when it changes
  useEffect(() => {
    if (clinicId) {
      setValue("clinic_id", clinicId);
    }
  }, [clinicId, setValue]);

  // Reset form when dialog opens/closes
  useEffect(() => {
    if (open) {
      if (isUpcomingContext) {
        reset(recordDefaultValues);
        const reminderOption = recordDefaultValues.item_id
          ? applicationOptions.find((option) => option.itemId === recordDefaultValues.item_id) ?? null
          : null;
        setVaccineSearch(reminderOption?.label ?? "");
        setNextAppEntries([]);
        setExtraDateInputs({});
        setNextDoseDateInput("");
        setIsNextDoseCalendarOpen(false);
        setExpiryDateInput("");
        setIsExpiryDateCalendarOpen(false);
        setSuggestedNextDoseDate(null);
        nextDoseManualRef.current = false;
      } else if (vaccination) {
        const vaccineItemId = getVaccineItemId();
        const nextApplicationItemId = getNextApplicationItemId();

        const resetValues = {
          ...vaccination,
          item_id: vaccineItemId,
          next_application_item_id: nextApplicationItemId,
          application_date: new Date(vaccination.application_date),
          expiry_date: vaccination.expiry_date ? new Date(vaccination.expiry_date) : undefined,
          next_dose_date: vaccination.next_dose_date ? new Date(vaccination.next_dose_date) : undefined,
          has_next_dose: Boolean(vaccination.has_next_dose ?? vaccination.next_dose_date),
          next_dose_time: shouldShowCreateAppointmentToggle && vaccinationAppointmentId ? getNextDoseTimeDefault() : "",
          clinic_id: clinicId || vaccination.clinic_id,
          appointment_id: vaccination.appointment_id || undefined,
          create_appointment: shouldShowCreateAppointmentToggle ? Boolean(vaccinationAppointmentId) : undefined,
        };
        reset(resetValues);
        if (vaccineItemId) {
          setValue("item_id", vaccineItemId, { shouldDirty: false, shouldTouch: false });
        }
        const displayName = getVaccinationDisplayName();
        if (displayName) {
          setVaccineSearch(displayName);
        }
        const nextDisplayName = getNextApplicationDisplayName();
        setNextAppEntries([]);
        setExtraDateInputs({});
        const nextDoseDateValue = vaccination.next_dose_date ? new Date(vaccination.next_dose_date) : null;
        setNextDoseDateInput(
          nextDoseDateValue && !Number.isNaN(nextDoseDateValue.getTime())
            ? formatISOToDisplay(format(nextDoseDateValue, "yyyy-MM-dd"))
            : "",
        );
        setIsNextDoseCalendarOpen(false);
        const expiryDateValue = vaccination.expiry_date ? new Date(vaccination.expiry_date) : null;
        setExpiryDateInput(
          expiryDateValue && !Number.isNaN(expiryDateValue.getTime())
            ? formatISOToDisplay(format(expiryDateValue, "yyyy-MM-dd"))
            : "",
        );
        setIsExpiryDateCalendarOpen(false);
        setSuggestedNextDoseDate(null);
        nextDoseManualRef.current = Boolean(vaccination.next_dose_date);
      } else {
        // New vaccination - reset to empty form
        // Use appointmentId and medicalRecordId from prop if available (when creating from consultation)
        reset({
          ...recordDefaultValues,
          pet_id: petId,
          clinic_id: clinicId,
          appointment_id: propAppointmentId || undefined,
          medical_record_id: propMedicalRecordId || undefined,
        });
        setVaccineSearch("");
        setNextAppEntries([]);
        setExtraDateInputs({});
        setNextDoseDateInput("");
        setIsNextDoseCalendarOpen(false);
        setExpiryDateInput("");
        setIsExpiryDateCalendarOpen(false);
        setSuggestedNextDoseDate(null);
        nextDoseManualRef.current = false;
      }
    } else {
      // When closing, reset to empty form
      reset({
        ...recordDefaultValues,
        pet_id: petId,
        clinic_id: clinicId,
        appointment_id: undefined,
      });
      setSuggestedNextDoseDate(null);
      setVaccineSearch("");
      setNextAppEntries([]);
        setExtraDateInputs({});
      setNextDoseDateInput("");
      setIsNextDoseCalendarOpen(false);
      setExpiryDateInput("");
      setIsExpiryDateCalendarOpen(false);
      nextDoseManualRef.current = false;
    }
  }, [
    clinicId,
    getNextDoseTimeDefault,
    getNextApplicationDisplayName,
    getNextApplicationItemId,
    getVaccinationDisplayName,
    getVaccineItemId,
    isUpcomingContext,
    open,
    petId,
    propAppointmentId,
    propMedicalRecordId,
    recordDefaultValues,
    reset,
    setValue,
    shouldShowCreateAppointmentToggle,
    vaccination,
    vaccinationAppointmentId,
    applicationOptions,
  ]);

  useEffect(() => {
    if (!open || !vaccination || !applications?.length) return;
    const vaccineItemId = getVaccineItemId();
    if (!vaccineItemId) return;

    const matched = applicationOptions.find((o) => o.itemId === vaccineItemId);
    if (!matched) return;

    setValue("item_id", matched.itemId);
  }, [applications?.length, applicationOptions, getVaccineItemId, open, setValue, vaccination]);

  const selectedItemId = watch("item_id");
  const nextApplicationItemId = watch("next_application_item_id");
  const applicationDate = watch("application_date");
  const nextDoseDate = watch("next_dose_date");
  const hasNextDose = watch("has_next_dose");
  const createAppointment = watch("create_appointment");
  const selectedApplicationOption = useMemo(
    () => applicationOptions.find((option) => option.itemId === selectedItemId) ?? null,
    [applicationOptions, selectedItemId],
  );
  const selectedNextApplicationOption = useMemo(
    () => applicationOptions.find((option) => option.itemId === nextApplicationItemId) ?? null,
    [applicationOptions, nextApplicationItemId],
  );
  const selectedAutocompleteValue = selectedItemId
    ? `item:${selectedItemId}`
    : "";
  const nextApplicationAutocompleteOptions = useMemo(
    () => applicationOptions.map((o) => ({ value: o.itemId, label: o.label || o.rawName })),
    [applicationOptions],
  );

  const resolveDefaultNextApplicationOption = useCallback((option: VaccineOption | null) => {
    if (!option) return null;
    const firstInterval = option.dose_intervals[0] ?? null;
    if (!firstInterval?.next_application_item_id) return option;
    return applicationOptions.find((candidate) => candidate.itemId === firstInterval.next_application_item_id) ?? option;
  }, [applicationOptions]);

  const buildSuggestedNextApplicationEntries = useCallback((option: VaccineOption, baseDateValue: Date | string | null | undefined) => {
    const suggestions = buildApplicationDoseSuggestions(option.dose_intervals, baseDateValue, option.itemId);
    return {
      primary: suggestions[0] ?? null,
      extras: suggestions.slice(1).map((suggestion) => {
        const suggestedOption = applicationOptions.find((candidate) => candidate.itemId === suggestion.itemId);
        return {
          ...makeNextApplicationEntry(),
          item_id: suggestion.itemId,
          item_label: suggestedOption?.label ?? suggestedOption?.rawName ?? "",
          due_date: setDateOnlyNoon(suggestion.dueDate),
        };
      }),
    };
  }, [applicationOptions]);

  // Initialize vaccine search when vaccination is loaded
  useEffect(() => {
    if (open) {
      setActiveTab(
        submissionMode === "deferred"
          ? initialTab
          : isUpcomingContext
            ? "record"
            : vaccination
              ? "record"
              : initialTab,
      );
    }
  }, [initialTab, isUpcomingContext, open, submissionMode, vaccination]);

  useEffect(() => {
    if (isUpcomingContext) {
      return;
    }
    if (vaccination && applicationOptions) {
      const vaccineItemId = getVaccineItemId();
      const selected = vaccineItemId
        ? applicationOptions.find((o) => o.itemId === vaccineItemId)
        : undefined;
      if (selected?.label) setVaccineSearch(selected.label);
    } else if (!vaccination && !selectedAutocompleteValue) {
      setVaccineSearch("");
    }
  }, [applicationOptions, getVaccineItemId, isUpcomingContext, selectedAutocompleteValue, vaccination]);


  const applySuggestedNextDose = useCallback((intervalOption: VaccineOption | null, baseDateValue: Date | string | null | undefined, force = false) => {
    const automaticSuggestions = intervalOption
      ? buildSuggestedNextApplicationEntries(intervalOption, baseDateValue)
      : { primary: null, extras: [] };
    const suggestedDate = automaticSuggestions.primary?.dueDate ?? null;
    setSuggestedNextDoseDate(suggestedDate);

    if (!hasNextDose) return;
    if (!force && nextDoseManualRef.current) return;

    if (!suggestedDate) {
      setValue("next_dose_date", null, { shouldDirty: force, shouldTouch: force, shouldValidate: true });
      setNextDoseDateInput("");
      if (shouldShowCreateAppointmentToggle) {
        setValue("create_appointment", false, { shouldDirty: force, shouldTouch: force });
        setValue("next_dose_time", "", { shouldDirty: force, shouldTouch: force });
      }
      return;
    }

    const nextDoseValue = setDateOnlyNoon(suggestedDate);
    if (automaticSuggestions.primary) {
      setValue("next_application_item_id", automaticSuggestions.primary.itemId, { shouldDirty: force, shouldTouch: force, shouldValidate: true });
    }
    setValue("next_dose_date", nextDoseValue, { shouldDirty: force, shouldTouch: force, shouldValidate: true });
    setNextDoseDateInput(formatISOToDisplay(format(nextDoseValue, "yyyy-MM-dd")));
    setNextAppEntries(automaticSuggestions.extras);
    setExtraDateInputs(
      Object.fromEntries(
        automaticSuggestions.extras
          .filter((entry) => entry.due_date)
          .map((entry) => [entry._key, formatISOToDisplay(format(entry.due_date as Date, "yyyy-MM-dd"))]),
      ),
    );
    nextDoseManualRef.current = false;
  }, [buildSuggestedNextApplicationEntries, hasNextDose, setValue, shouldShowCreateAppointmentToggle]);

  useEffect(() => {
    if (!hasNextDose) {
      setSuggestedNextDoseDate(null);
      return;
    }

    if (!selectedApplicationOption || !applicationDate || nextDoseManualRef.current) {
      const firstIntervalForSuggestion = selectedApplicationOption?.dose_intervals[0] ?? null;
      setSuggestedNextDoseDate(applicationDate ? calculateDoseIntervalDate(new Date(applicationDate), firstIntervalForSuggestion) : null);
      return;
    }

    applySuggestedNextDose(selectedApplicationOption, applicationDate);
  }, [applicationDate, applySuggestedNextDose, hasNextDose, selectedApplicationOption]);

  useEffect(() => {
    if (!shouldShowCreateAppointmentToggle) return;
    if (!hasNextDose || !createAppointment) {
      setValue("next_dose_time", "");
      const current = getValues("next_dose_date");
      if (current instanceof Date) {
        setValue("next_dose_date", setDateOnlyNoon(current), { shouldDirty: true, shouldTouch: true });
      }
    }
  }, [createAppointment, getValues, hasNextDose, setValue, shouldShowCreateAppointmentToggle]);

  const performSubmit = async (data: PetVaccinationFormInput, cancelAppointment?: boolean) => {
    try {
      const shouldSchedule = shouldShowCreateAppointmentToggle ? Boolean(data.has_next_dose && data.create_appointment) : false;
      const normalizedNextDoseDate =
        data.next_dose_date instanceof Date
          ? data.next_dose_date
          : data.next_dose_date
            ? new Date(data.next_dose_date)
            : null;
      const dateOnly = normalizedNextDoseDate ? setDateOnlyNoon(normalizedNextDoseDate) : null;
      const combined = shouldSchedule && dateOnly ? combineDateAndTime(dateOnly, data.next_dose_time ?? "") : dateOnly;

      const itemId = data.item_id?.trim() ? data.item_id.trim() : null;
      const nextApplicationId =
        data.has_next_dose && data.next_application_item_id?.trim()
          ? data.next_application_item_id.trim()
          : null;

      if (!itemId) {
        console.error(t("vaccines.form.applicationRequired"));
        return;
      }

      if (!clinicId) {
        console.error(t("vaccines.form.clinicUndefined"));
        return;
      }

      // Use appointment_id from form data, prop, or reminder context
      const finalAppointmentId =
        data.appointment_id
        || propAppointmentId
        || recordReminder?.appointment_id
        || vaccination?.appointment_id
        || undefined;

      const createAppointmentPayload = shouldShowCreateAppointmentToggle
        ? { create_appointment: shouldSchedule }
        : {};

      // UI-only field
      const { next_dose_time: _nextDoseTime, ...dataWithoutNextDoseTime } = data;

      const submitData = {
        ...dataWithoutNextDoseTime,
        pet_id: upcomingContext?.petId ?? petId,
        clinic_id: clinicId, // Always use current clinic
        item_id: itemId,
        next_application_item_id: data.has_next_dose ? nextApplicationId : null,
        has_next_dose: Boolean(data.has_next_dose),
        appointment_id: data.has_next_dose ? finalAppointmentId : null, // Link to appointment if available
        medical_record_id: (!isUpcomingContext ? vaccination?.medical_record_id : undefined) || propMedicalRecordId || undefined,
        ...createAppointmentPayload,
        application_date: data.application_date instanceof Date
          ? data.application_date.toISOString()
          : data.application_date,
        expiry_date: data.expiry_date instanceof Date
          ? format(data.expiry_date, "yyyy-MM-dd")
          : data.expiry_date,
        next_dose_date: data.has_next_dose
          ? combined instanceof Date
            ? combined.toISOString()
            : combined ?? null
          : null,
      };

      console.log("Submitting data:", submitData);

      const draftPayload: PetApplicationFormDraft = {
        ...submitData,
        next_applications: nextAppEntries,
        item:
          selectedApplicationOption
            ? { name: selectedApplicationOption.label }
            : vaccination?.item ?? null,
        next_application_item:
          selectedNextApplicationOption
            ? { name: selectedNextApplicationOption.label }
            : vaccination?.next_application_item ?? null,
      };

      const savedApplication = await dispatchApplicationSubmission({
        mode: submissionMode,
        draft: draftPayload,
        onDraftSubmit,
        persist: async () =>
          vaccination?.id && !isUpcomingContext
            ? updateVaccination.mutateAsync({
              id: vaccination.id,
              ...submitData,
              next_applications: nextAppEntries,
              cancelAppointment,
            })
            : createVaccination.mutateAsync({
              ...submitData,
              next_applications: nextAppEntries,
            }),
      });

      if (submissionMode === "deferred") {
        reset(recordDefaultValues);
        setNextAppEntries([]);
        setExtraDateInputs({});
        onOpenChange(false);
        return;
      }

      if (isUpcomingContext) {
        if (resolvedPlan?.id) {
          await completePlan.mutateAsync({
            planId: resolvedPlan.id,
            completedPetApplicationId: savedApplication!.id,
          });
        } else if (recordReminder?.id) {
          await updateVaccination.mutateAsync({
            id: recordReminder.id,
            has_next_dose: false,
            next_dose_completed_at: new Date().toISOString(),
            next_dose_completed_by_application_id: savedApplication.id,
            create_appointment: false,
            cancelAppointment: Boolean(recordReminder.appointment_id),
          });
        }
      }

      onApplicationSaved?.({
        ...(vaccination ?? {}),
        ...submitData,
        ...(savedApplication ?? {}),
        id: (savedApplication as { id?: string } | null | undefined)?.id ?? vaccination?.id,
        item:
          selectedApplicationOption
            ? {
              name: selectedApplicationOption.label,
            }
            : vaccination?.item ?? null,
        next_application_item:
          selectedNextApplicationOption
            ? {
              name: selectedNextApplicationOption.label,
            }
            : vaccination?.next_application_item ?? null,
        appointment:
          submitData.appointment_id && combined instanceof Date
            ? {
              starts_at: combined.toISOString(),
            }
            : vaccination?.appointment ?? null,
      });

      reset(recordDefaultValues);
      setNextAppEntries([]);
        setExtraDateInputs({});
      onOpenChange(false);
    } catch (error) {
      console.error("Error saving vaccination:", error);
    }
  };

  const onSubmit = async (data: PetVaccinationFormInput) => {
    // Check if we're editing and next_dose_date was removed
    const hadNextDoseDate = vaccination?.next_dose_date;
    const hasNextDoseDate = data.next_dose_date;
    const isRemovingNextDoseDate = hadNextDoseDate && !hasNextDoseDate;
    const isDisablingCreateAppointment =
      shouldShowCreateAppointmentToggle && Boolean(vaccinationAppointmentId) && data.create_appointment === false;

    if (isRemovingNextDoseDate && vaccination?.appointment_id) {
      // Show confirmation dialog
      setPendingSubmitData(data);
      setAppointmentConfirmReason("remove_next_dose_date");
      setShowCancelAppointmentDialog(true);
    } else if (isDisablingCreateAppointment) {
      setPendingSubmitData(data);
      setAppointmentConfirmReason("disable_create_appointment");
      setShowCancelAppointmentDialog(true);
    } else {
      // Submit directly
      await performSubmit(data);
    }
  };

  const handleCancelAppointmentConfirm = async (cancelAppointment: boolean) => {
    setShowCancelAppointmentDialog(false);
    if (pendingSubmitData) {
      await performSubmit(pendingSubmitData, cancelAppointment);
      setPendingSubmitData(null);
      setAppointmentConfirmReason(null);
    }
  };

  const handleConfirmDelete = async () => {
    if (!vaccination?.id) return;

    try {
      if (submissionMode === "deferred") {
        onDraftDelete?.(vaccination.id);
        setShowDeleteDialog(false);
        onOpenChange(false);
        return;
      }

      if (vaccinationDeleteMode === "clear_next_dose_reminder") {
        await updateVaccination.mutateAsync({
          id: vaccination.id,
          has_next_dose: false,
          next_dose_date: null,
          next_application_item_id: null,
          create_appointment: false,
          cancelAppointment: true,
        });
      } else {
        await deleteVaccination.mutateAsync(vaccination.id);
        onApplicationDeleted?.(vaccination.id);
      }
      setShowDeleteDialog(false);
      onOpenChange(false);
    } catch (error) {
      console.error("Error deleting application:", error);
    }
  };

  const modalTitle = isUpcomingContext
    ? activeTab === "plan"
      ? t("vaccines.planForm.editTitle")
      : t("patients:patientActions.registerApplication")
    : vaccination
    ? t("vaccines.form.editTitle")
    : resolvedPlan
      ? t("vaccines.planForm.editTitle")
    : activeTab === "plan"
      ? t("vaccines.planForm.title")
      : t("vaccines.form.register");
  const isEditMode = Boolean(vaccination || resolvedPlan);

  return (
    <>
      <ResponsiveDialog
        open={open}
        onOpenChange={onOpenChange}
        noScrollViewport
        className="flex max-h-[90vh] max-w-2xl flex-col p-0"
      >
        <div className="shrink-0 border-b border-border/60 bg-white px-6 py-4">
          <DialogHeader className="text-left">
            <div className="flex items-center justify-between">
              <DialogTitle>{modalTitle}</DialogTitle>
            </div>
          </DialogHeader>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-6 py-4">
        {!isEditMode || isUpcomingContext ? (
          <SegmentedTabs
            value={activeTab}
            onValueChange={(value) => setActiveTab(value as "record" | "plan")}
            className="space-y-4"
            width="full"
            items={[
              {
                value: "record",
                label: isUpcomingContext ? t("patients:patientActions.registerApplication") : t("vaccines.modalTabs.recordNow"),
              },
              {
                value: "plan",
                label: isUpcomingContext ? t("vaccines.planForm.editTitle") : t("vaccines.modalTabs.createReminder"),
              },
            ]}
          />
        ) : null}

        {activeTab === "plan" ? (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
            </p>
            <ApplicationPlanForm
              petId={upcomingContext?.petId ?? petId}
              clinicId={clinicId}
              petSpeciesId={petSpeciesId}
              appointmentId={propAppointmentId}
              plan={resolvedPlan}
              onCancel={() => onOpenChange(false)}
              onSuccess={() => {
                onPlanSaved?.();
                onOpenChange(false);
              }}
              footerContainer={footerEl}
              submissionMode={submissionMode}
              onDraftSubmit={onPlanDraftSubmit}
              onDraftDelete={onPlanDraftDelete}
            />
          </div>
        ) : (
        <form onSubmit={handleSubmit(onSubmit, (errors) => {
          console.error("Form validation errors:", errors);
          Object.keys(errors).forEach((key) => {
            const error = errors[key as keyof typeof errors];
            if (error?.message) {
              console.error(`${key}: ${error.message}`);
            }
          });
        })} id={recordFormId} className="space-y-4">
          <input type="hidden" {...register("item_id")} />
          <input type="hidden" {...register("next_application_item_id")} />
          <input type="hidden" {...register("clinic_id", { value: clinicId })} />
          <input type="hidden" {...register("pet_id", { value: upcomingContext?.petId ?? petId })} />
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <Label htmlFor="item_id">{t("vaccines.form.application")} *</Label>
              <ApplicationAutocomplete
                options={applicationOptions}
                isLoading={isApplicationsLoading}
                value={vaccineSearch}
                selectedValue={selectedAutocompleteValue}
                onChange={(value) => {
                  setVaccineSearch(value);
                  // Limpar seleção se o texto mudar
                  const current = applicationOptions.find((o) => o.value === selectedAutocompleteValue);
                  if (current) {
                    const valueNorm = value.toLowerCase().trim();
                    const rawNorm = (current.rawName ?? "").toLowerCase().trim();
                    const labelNorm = (current.label ?? current.rawName ?? "").toLowerCase().trim();
                    const matchesCurrent =
                      (rawNorm && valueNorm.includes(rawNorm)) || (labelNorm && valueNorm === labelNorm);

                    if (!matchesCurrent) {
                      setValue("item_id", null);
                      setSuggestedNextDoseDate(null);
                    }
                  }
                }}
                onSelectOption={(option, displayName) => {
                  setVaccineSearch(displayName);
                  setValue("item_id", option.itemId);
                  const applicationDateValue = getValues("application_date");
                  const automaticSuggestions = buildSuggestedNextApplicationEntries(option, applicationDateValue);
                  nextDoseManualRef.current = false;

                  if (automaticSuggestions.primary) {
                    const nextDoseValue = setDateOnlyNoon(automaticSuggestions.primary.dueDate);
                    setValue("has_next_dose", true, { shouldDirty: true, shouldTouch: true, shouldValidate: true });
                    setValue("next_application_item_id", automaticSuggestions.primary.itemId, { shouldDirty: true, shouldTouch: true, shouldValidate: true });
                    setValue("next_dose_date", nextDoseValue, { shouldDirty: true, shouldTouch: true, shouldValidate: true });
                    setNextDoseDateInput(formatISOToDisplay(format(nextDoseValue, "yyyy-MM-dd")));
                    setSuggestedNextDoseDate(automaticSuggestions.primary.dueDate);
                    setNextAppEntries(automaticSuggestions.extras);
                    setExtraDateInputs(
                      Object.fromEntries(
                        automaticSuggestions.extras
                          .filter((entry) => entry.due_date)
                          .map((entry) => [entry._key, formatISOToDisplay(format(entry.due_date as Date, "yyyy-MM-dd"))]),
                      ),
                    );
                  } else {
                    const suggestedNextApplication = resolveDefaultNextApplicationOption(option) ?? option;
                    setValue("next_application_item_id", suggestedNextApplication.itemId, { shouldDirty: true, shouldTouch: true, shouldValidate: true });
                    applySuggestedNextDose(option, applicationDateValue, true);
                    // Auto-populate multi next-applications from catalog defaults
                    const defaults = option.next_application_defaults;
                    if (defaults && defaults.length > 0) {
                      const appDate = applicationDateValue;
                      setNextAppEntries(buildEntriesFromDefaults(defaults, appDate instanceof Date ? appDate : new Date()));
                    } else {
                      setNextAppEntries([]);
                      setExtraDateInputs({});
                    }
                  }
                  // Auto-set dose_number to 1 when vaccine changes
                  if (!vaccination) {
                    setValue("dose_number", 1);
                  }
                }}
                onOpenCreateDialog={submissionMode === "immediate"
                  ? (name) => {
                    setVaccineSearch(name);
                    setCreateVaccineDialogOpen(true);
                  }
                  : undefined}
                placeholder={t("vaccines.form.applicationPlaceholder")}
                popoverPortalled={false}
              />
              {selectedApplicationOption?.isDeleted ? (
                <p className="mt-1 text-xs text-muted-foreground">
                  {t("vaccines.autocomplete.deletedItemDescription")}
                </p>
              ) : null}
              {errors.item_id && (
                <p className="text-sm text-destructive mt-1">{t(String(errors.item_id.message))}</p>
              )}
            </div>

            <div className="col-span-2">
              <Label htmlFor="application_date">{t("vaccines.form.applicationDateTime")} *</Label>
              <Controller
                name="application_date"
                control={control}
                render={({ field }) => (
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        type="button"
                        variant="outline"
                        className={cn(
                          "flex md:w-fit pl-3 text-left font-normal",
                          !field.value && "text-muted-foreground"
                        )}
                      >
                        {field.value ? (
                          format(field.value instanceof Date ? field.value : new Date(field.value), "dd/MM/yyyy 'às' HH:mm")
                        ) : (
                          <span>{t("vaccines.form.selectDateTime")}</span>
                        )}
                        {field.value ? (
                          <X
                            className="ml-auto h-4 w-4 opacity-50 hover:opacity-100"
                            onClick={(e) => {
                              e.stopPropagation();
                              const newDate = new Date();
                              field.onChange(newDate);
                              applySuggestedNextDose(selectedApplicationOption, newDate);
                            }}
                          />
                        ) : (
                          <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                        )}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-full p-0" align="start">
                      <div className="flex flex-col gap-4 p-3 sm:flex-row sm:items-start">
                        <Calendar
                          mode="single"
                          selected={field.value ? new Date(field.value) : undefined}
                          onSelect={(date) => {
                            if (!date) {
                              field.onChange(new Date());
                              return;
                            }

                            const currentValue = field.value ? new Date(field.value) : new Date();
                            const updated = new Date(
                              date.getFullYear(),
                              date.getMonth(),
                              date.getDate(),
                              currentValue.getHours(),
                              currentValue.getMinutes(),
                              0,
                              0,
                            );

                            field.onChange(updated);
                            applySuggestedNextDose(selectedApplicationOption, updated);
                          }}
                          disabled={(date) => date > new Date()}
                          initialFocus
                        />
                        <div className="flex min-w-[160px] flex-col gap-2">
                          <p className="text-sm font-medium text-foreground">{t("vaccines.form.time")}</p>
                          <Input
                            type="time"
                            value={
                              field.value
                                ? format(field.value instanceof Date ? field.value : new Date(field.value), "HH:mm")
                                : ""
                            }
                            onChange={(event) => {
                              const timeValue = event.target.value;
                              if (!timeValue) {
                                return;
                              }

                              const [hours, minutes] = timeValue.split(":").map(Number);
                              const base = field.value ? new Date(field.value) : new Date();
                              const updated = new Date(base);
                              updated.setHours(hours, minutes, 0, 0);
                              field.onChange(updated);
                              applySuggestedNextDose(selectedApplicationOption, updated);
                            }}
                          />
                          <Button
                            variant="outline"
                            type="button"
                            onClick={() => {
                              const newDate = new Date();
                              field.onChange(newDate);
                              applySuggestedNextDose(selectedApplicationOption, newDate);
                            }}
                          >
                            {t("vaccines.form.useCurrentTime")}
                          </Button>
                          <Button
                            variant="ghost"
                            type="button"
                            onClick={() => {
                              const newDate = new Date();
                              field.onChange(newDate);
                              applySuggestedNextDose(selectedApplicationOption, newDate);
                            }}
                          >
                            {t("vaccines.form.clear")}
                          </Button>
                        </div>
                      </div>
                    </PopoverContent>
                  </Popover>
                )}
              />
              {errors.application_date && (
                <p className="text-sm text-destructive mt-1">{t(String(errors.application_date.message))}</p>
              )}
            </div>

            <div>
              <Label htmlFor="dose_number">{t("vaccines.form.doseNumber")} *</Label>
              <Input
                id="dose_number"
                type="number"
                min="1"
                {...register("dose_number", { valueAsNumber: true })}
              />
            </div>

            <div>
              <Label htmlFor="batch_number">{t("vaccines.form.batch")}</Label>
              <Input id="batch_number" {...register("batch_number")} />
            </div>

            <div>
              <Label htmlFor="expiry_date">{t("vaccines.form.batchExpiry")}</Label>
              <Controller
                name="expiry_date"
                control={control}
                render={({ field }) => (
                  <div className="flex items-center gap-2">
                    <Input
                      id="expiry_date"
                      placeholder={t("vaccines.form.datePlaceholder")}
                      inputMode="numeric"
                      ref={expiryDateInputRef}
                      value={expiryDateInput}
                      onChange={(event) => {
                        const rawValue = event.target.value;
                        captureExpiryDateCaret(rawValue, event.target.selectionStart);

                        const maskedValue = maskDateInput(rawValue);
                        setExpiryDateInput(maskedValue);
                        if (!maskedValue) {
                          field.onChange(undefined);
                          return;
                        }

                        const isoValue = convertDisplayToISO(maskedValue);
                        if (!isoValue) return;

                        const [year, month, day] = isoValue.split("-");
                        if (!year || !month || !day) return;
                        const parsed = new Date(Number(year), Number(month) - 1, Number(day));
                        field.onChange(parsed);
                      }}
                      onBlur={() => {
                        const isoValue = convertDisplayToISO(expiryDateInput);
                        if (!isoValue) {
                          field.onChange(undefined);
                          setExpiryDateInput("");
                          return;
                        }
                        setExpiryDateInput(formatISOToDisplay(isoValue));
                      }}
                      className={cn("!flex-1", getDateErrorMessage(expiryDateInput) && "border-destructive")}
                    />

                    <DateFieldPopover
                      open={isExpiryDateCalendarOpen}
                      onOpenChange={setIsExpiryDateCalendarOpen}
                      trigger={
                        <Button
                          type="button"
                          variant="outline"
                          size="icon"
                          className="h-10 w-10"
                          aria-label={t("vaccines.form.selectExpiryDate")}
                        >
                          <CalendarIcon className="h-4 w-4 opacity-70" />
                        </Button>
                      }
                      selected={field.value ? new Date(field.value) : undefined}
                      onSelect={(date) => {
                        if (!date) return;
                        const iso = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
                        setExpiryDateInput(formatISOToDisplay(iso));
                        field.onChange(new Date(date.getFullYear(), date.getMonth(), date.getDate()));
                      }}
                    />
                  </div>
                )}
              />
              {getDateErrorMessage(expiryDateInput) ? (
                <p className="mt-1 text-sm text-destructive">{getDateErrorMessage(expiryDateInput)}</p>
              ) : null}
            </div>

            {/* Suggested Next Dose Date */}
            {/* {suggestedNextDoseDate && (
              <div className="col-span-2">
                <Alert>
                  <Info className="h-4 w-4" />
                  <AlertDescription>
                    Data sugerida para próxima dose:{" "}
                    <strong>
                      {format(suggestedNextDoseDate, "dd/MM/yyyy", {
                        locale: ptBR,
                      })}
                    </strong>
                  </AlertDescription>
                </Alert>
              </div>
            )} */}

            <div className="col-span-2">
              <div className="rounded-md border border-border/60">
                <div className="flex items-center justify-between px-3 py-2">
                  <div>
                    <p className="text-sm font-medium">{t("vaccines.form.hasNextDose")}</p>
                    <p className="text-xs text-muted-foreground">{t("vaccines.form.hasNextDoseDescription")}</p>
                  </div>
                  <Controller
                    name="has_next_dose"
                    control={control}
                    render={({ field }) => (
                      <Switch
                        checked={Boolean(field.value)}
                        onCheckedChange={(checked) => {
                          const enabled = Boolean(checked);
                          field.onChange(enabled);

                          if (enabled) {
                            const fallbackOption = selectedApplicationOption;
                            if (fallbackOption && !getValues("next_application_item_id")) {
                              const suggestedNextApplication = resolveDefaultNextApplicationOption(fallbackOption) ?? fallbackOption;
                              setValue("next_application_item_id", suggestedNextApplication.itemId, { shouldDirty: true, shouldTouch: true, shouldValidate: true });
                              nextDoseManualRef.current = false;
                              applySuggestedNextDose(fallbackOption, getValues("application_date"), true);
                            }
                            return;
                          }

                          nextDoseManualRef.current = false;
                          setValue("next_application_item_id", null, { shouldDirty: true, shouldTouch: true, shouldValidate: true });
                          setValue("next_dose_date", null, { shouldDirty: true, shouldTouch: true, shouldValidate: true });
                          setValue("create_appointment", false, { shouldDirty: true, shouldTouch: true });
                          setValue("next_dose_time", "", { shouldDirty: true, shouldTouch: true });
                          setNextDoseDateInput("");
                          setSuggestedNextDoseDate(null);
                        }}
                      />
                    )}
                  />
                </div>

                {hasNextDose ? (
                  <div className="border-t border-border/60 px-3 py-3">
                    <div className="mb-3">
                      <Label htmlFor="next_application_item_id">{t("vaccines.form.nextApplication")} *</Label>
                      <FiscalCombobox
                        value={nextApplicationItemId ?? null}
                        onChange={(value) => {
                          setValue("next_application_item_id", value, { shouldDirty: true, shouldTouch: true, shouldValidate: true });
                          nextDoseManualRef.current = false;
                          applySuggestedNextDose(selectedApplicationOption, getValues("application_date"), true);
                        }}
                        options={nextApplicationAutocompleteOptions}
                        placeholder={t("vaccines.form.nextApplicationPlaceholder")}
                      />
                      {selectedNextApplicationOption?.isDeleted ? (
                        <p className="mt-1 text-xs text-muted-foreground">
                          {t("vaccines.autocomplete.deletedItemDescription")}
                        </p>
                      ) : null}
                      {errors.next_application_item_id && (
                        <p className="text-sm text-destructive mt-1">{t(String(errors.next_application_item_id.message))}</p>
                      )}
                    </div>

                    <div className="mt-2 grid grid-cols-1 gap-3 md:grid-cols-[1fr_200px_150px]">
                      <div className={cn(!shouldShowCreateAppointmentToggle && "md:col-span-3")}>
                        <Label htmlFor="next_dose_date">{t("vaccines.form.nextDose")}</Label>
                        <Controller
                          name="next_dose_date"
                          control={control}
                          render={({ field }) => (
                            <div className="flex items-center gap-2">
                              <Input
                                id="next_dose_date"
                                placeholder={t("vaccines.form.datePlaceholder")}
                                inputMode="numeric"
                                ref={nextDoseDateInputRef}
                                value={nextDoseDateInput}
                                onChange={(event) => {
                                  const rawValue = event.target.value;
                                  captureNextDoseDateCaret(rawValue, event.target.selectionStart);

                                  const maskedValue = maskDateInput(rawValue);
                                  setNextDoseDateInput(maskedValue);
                                  if (!maskedValue) {
                                    nextDoseManualRef.current = true;
                                    setValue("next_dose_date", null, { shouldDirty: true, shouldTouch: true, shouldValidate: true });
                                    field.onChange(null);
                                    if (shouldShowCreateAppointmentToggle) {
                                      setValue("create_appointment", false, { shouldDirty: true, shouldTouch: true });
                                      setValue("next_dose_time", "", { shouldDirty: true, shouldTouch: true });
                                    }
                                    return;
                                  }
                                  const isoValue = convertDisplayToISO(maskedValue);
                                  if (!isoValue) {
                                    return;
                                  }

                                  const [year, month, day] = isoValue.split("-");
                                  if (!year || !month || !day) return;
                                  const parsed = new Date(Number(year), Number(month) - 1, Number(day));
                                  const dateValue = setDateOnlyNoon(parsed);
                                  nextDoseManualRef.current = true;
                                  setValue("next_dose_date", dateValue, { shouldDirty: true, shouldTouch: true, shouldValidate: true });
                                  field.onChange(dateValue);
                                }}
                                onBlur={() => {
                                  const isoValue = convertDisplayToISO(nextDoseDateInput);
                                  if (!isoValue) {
                                    nextDoseManualRef.current = true;
                                    setValue("next_dose_date", null, { shouldDirty: true, shouldTouch: true, shouldValidate: true });
                                    field.onChange(null);
                                    setNextDoseDateInput("");
                                    if (shouldShowCreateAppointmentToggle) {
                                      setValue("create_appointment", false, { shouldDirty: true, shouldTouch: true });
                                      setValue("next_dose_time", "", { shouldDirty: true, shouldTouch: true });
                                    }
                                    return;
                                  }
                                  setNextDoseDateInput(formatISOToDisplay(isoValue));
                                }}
                                className={cn(
                                  "!flex-1",
                                  (getDateErrorMessage(nextDoseDateInput) || errors.next_dose_date?.message) && "border-destructive",
                                )}
                              />
                              <DateFieldPopover
                                open={isNextDoseCalendarOpen}
                                onOpenChange={setIsNextDoseCalendarOpen}
                                trigger={
                                  <Button
                                    type="button"
                                    variant="outline"
                                    size="icon"
                                    className="h-10 w-10"
                                    aria-label={t("vaccines.form.selectDate")}
                                  >
                                    <CalendarIcon className="h-4 w-4 opacity-70" />
                                  </Button>
                                }
                                selected={field.value ? new Date(field.value) : undefined}
                                onSelect={(date) => {
                                  if (!date) return;
                                  const iso = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
                                  setNextDoseDateInput(formatISOToDisplay(iso));
                                  const dateValue = setDateOnlyNoon(date);
                                  nextDoseManualRef.current = true;
                                  setValue("next_dose_date", dateValue, { shouldDirty: true, shouldTouch: true, shouldValidate: true });
                                  field.onChange(dateValue);
                                }}
                              />
                            </div>
                          )}
                        />
                        {getDateErrorMessage(nextDoseDateInput) ? (
                          <p className="mt-1 text-sm text-destructive">{getDateErrorMessage(nextDoseDateInput)}</p>
                        ) : null}
                      </div>

                      {shouldShowCreateAppointmentToggle && nextDoseDate ? (
                        <Controller
                          name="create_appointment"
                          control={control}
                          render={({ field }) => (
                            <div className="flex items-center justify-between rounded-md border border-border/60 px-3 py-2 md:mt-6">
                              <span className="text-sm text-muted-foreground">{t("vaccines.form.createAppointment")}</span>
                              <Switch
                                checked={Boolean(field.value)}
                                onCheckedChange={(nextChecked) => {
                                  const nextValue = Boolean(nextChecked);
                                  field.onChange(nextValue);
                                  if (!nextValue) {
                                    setValue("next_dose_time", "", { shouldDirty: true, shouldTouch: true });
                                  }
                                }}
                              />
                            </div>
                          )}
                        />
                      ) : null}

                      {shouldShowCreateAppointmentToggle && nextDoseDate && createAppointment ? (
                        <div>
                          <Label htmlFor="next_dose_time">{t("vaccines.form.time")} *</Label>
                          <Controller
                            name="next_dose_time"
                            control={control}
                            render={({ field }) => (
                              <Input
                                id="next_dose_time"
                                type="time"
                                value={field.value ?? ""}
                                onChange={(e) => field.onChange(e.target.value)}
                              />
                            )}
                          />
                          {errors.next_dose_time?.message && (
                            <p className="mt-1 text-sm text-destructive">{t(String(errors.next_dose_time.message))}</p>
                          )}
                        </div>
                      ) : null}
                    </div>

                    {nextAppEntries.map((entry, index) => (
                      <div key={entry._key} className="mt-3 space-y-3 border-t border-border/40 pt-3">
                        <div className="flex items-start gap-2">
                          <div className="flex-1 space-y-3">
                            <div className="flex items-center justify-between">
                              <div className="flex flex-col w-full">
                                <Label className="mb-1 block">{t("vaccines.form.nextApplication")}</Label>
                                <FiscalCombobox
                                  value={entry.item_id}
                                  onChange={(value) =>
                                    setNextAppEntries((prev) =>
                                      prev.map((e, i) => i === index ? { ...e, item_id: value, item_label: nextApplicationAutocompleteOptions.find((o) => o.value === value)?.label ?? "" } : e)
                                    )
                                  }
                                  options={nextApplicationAutocompleteOptions}
                                  placeholder={t("vaccines.form.nextApplicationPlaceholder")}
                                />
                              </div>

                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                className="mt-[18px] shrink-0"
                                onClick={() => setNextAppEntries((prev) => prev.filter((_, i) => i !== index))}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>

                            <div className={cn("grid grid-cols-1 gap-3", shouldShowCreateAppointmentToggle && entry.due_date ? "md:grid-cols-[1fr_200px_150px]" : "md:grid-cols-1")}>
                              <div className={cn(!shouldShowCreateAppointmentToggle && "md:col-span-3")}>
                                <Label className="mb-1 block">{t("vaccines.form.nextDose")}</Label>
                                <div className="flex items-center gap-2">
                                  <Input
                                    placeholder={t("vaccines.form.datePlaceholder")}
                                    inputMode="numeric"
                                    value={extraDateInputs[entry._key] ?? ""}
                                    onChange={(event) => {
                                      const maskedValue = maskDateInput(event.target.value);
                                      setExtraDateInputs((prev) => ({ ...prev, [entry._key]: maskedValue }));
                                      if (!maskedValue) {
                                        setNextAppEntries((prev) => prev.map((e, i) => i === index ? { ...e, due_date: null } : e));
                                        return;
                                      }
                                      const isoValue = convertDisplayToISO(maskedValue);
                                      if (!isoValue) return;
                                      const [y, m, d] = isoValue.split("-");
                                      if (!y || !m || !d) return;
                                      setNextAppEntries((prev) => prev.map((e, i) => i === index ? { ...e, due_date: setDateOnlyNoon(new Date(Number(y), Number(m) - 1, Number(d))) } : e));
                                    }}
                                    onBlur={() => {
                                      const isoValue = convertDisplayToISO(extraDateInputs[entry._key] ?? "");
                                      if (!isoValue) {
                                        setExtraDateInputs((prev) => ({ ...prev, [entry._key]: "" }));
                                        setNextAppEntries((prev) => prev.map((e, i) => i === index ? { ...e, due_date: null } : e));
                                        return;
                                      }
                                      setExtraDateInputs((prev) => ({ ...prev, [entry._key]: formatISOToDisplay(isoValue) }));
                                    }}
                                    className="!flex-1"
                                  />
                                  <DateFieldPopover
                                    trigger={
                                      <Button type="button" variant="outline" size="icon" className="h-10 w-10">
                                        <CalendarIcon className="h-4 w-4 opacity-70" />
                                      </Button>
                                    }
                                    selected={entry.due_date ?? undefined}
                                    onSelect={(date) => {
                                      if (!date) return;
                                      const iso = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
                                      setExtraDateInputs((prev) => ({ ...prev, [entry._key]: formatISOToDisplay(iso) }));
                                      setNextAppEntries((prev) => prev.map((e, i) => i === index ? { ...e, due_date: setDateOnlyNoon(date) } : e));
                                    }}
                                  />
                                </div>
                              </div>

                              {shouldShowCreateAppointmentToggle && entry.due_date ? (
                                <div className="flex items-center justify-between rounded-md border border-border/60 px-3 py-2 md:mt-4">
                                  <span className="text-sm text-muted-foreground">{t("vaccines.form.createAppointment")}</span>
                                  <Switch
                                    checked={Boolean(entry.create_appointment)}
                                    onCheckedChange={(checked) =>
                                      setNextAppEntries((prev) =>
                                        prev.map((e, i) => i === index ? { ...e, create_appointment: checked, due_time: checked ? e.due_time : "" } : e)
                                      )
                                    }
                                  />
                                </div>
                              ) : null}

                              {shouldShowCreateAppointmentToggle && entry.due_date && entry.create_appointment ? (
                                <div>
                                  <Label className="mb-1 block">{t("vaccines.form.time")} *</Label>
                                  <Input
                                    type="time"
                                    value={entry.due_time ?? ""}
                                    onChange={(e) =>
                                      setNextAppEntries((prev) =>
                                        prev.map((ex, i) => i === index ? { ...ex, due_time: e.target.value } : ex)
                                      )
                                    }
                                  />
                                </div>
                              ) : null}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}

                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="mt-3 w-full"
                      onClick={() => setNextAppEntries((prev) => [...prev, makeNextApplicationEntry()])}
                    >
                      <Plus className="mr-1 h-4 w-4" />
                      {t("vaccines.form.addNextApplication")}
                    </Button>
                  </div>
                ) : null}
              </div>

              {errors.has_next_dose?.message && (
                <p className="text-sm text-destructive mt-1">{t(String(errors.has_next_dose.message))}</p>
              )}
              {errors.next_dose_date?.message && (
                <p className="text-sm text-destructive mt-1">{t(String(errors.next_dose_date.message))}</p>
              )}
              {hasNextDose && suggestedNextDoseDate && hasDoseInterval(selectedApplicationOption?.dose_intervals[0] ?? null) ? (
                <p className="text-xs text-color-light mt-1">{t("vaccines.form.nextDoseAutoFilled")}</p>
              ) : null}
            </div>

            <div className="col-span-2">
              <Label htmlFor="adverse_reaction">{t("vaccines.form.adverseReaction")}</Label>
              <Textarea
                id="adverse_reaction"
                {...register("adverse_reaction")}
                rows={2}
                placeholder={t("vaccines.form.adverseReactionPlaceholder")}
              />
            </div>

            <div className="col-span-2">
              <Label htmlFor="notes">{t("vaccines.form.notes")}</Label>
              <Textarea
                id="notes"
                {...register("notes")}
                rows={2}
                placeholder={t("vaccines.form.notesPlaceholder")}
              />
            </div>
          </div>

          {footerEl && createPortal(
            <>
              <div>
                {vaccination?.id ? (
                  <Button
                    type="button"
                    variant="destructive"
                    onClick={() => setShowDeleteDialog(true)}
                    className="cursor-pointer disabled:cursor-not-allowed"
                    disabled={deleteVaccination.isPending || updateVaccination.isPending}
                  >
                    {(deleteVaccination.isPending || updateVaccination.isPending)
                      ? t("vaccines.history.deleteDialog.removing")
                      : t("vaccines.history.delete")}
                  </Button>
                ) : null}
              </div>
              <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                className="cursor-pointer disabled:cursor-not-allowed"
              >
                {t("vaccines.history.deleteDialog.cancel")}
              </Button>
              <Button
                type="submit"
                form={recordFormId}
                className="cursor-pointer disabled:cursor-not-allowed"
                disabled={createVaccination.isPending || updateVaccination.isPending || deleteVaccination.isPending}
              >
                {vaccination ? t("vaccines.form.update") : t("vaccines.form.register")}
              </Button>
              </div>
            </>,
            footerEl,
          )}
        </form>
        )}
        </div>

        <div className="shrink-0 border-t border-border/60 bg-white px-6 py-4">
          <div ref={setFooterEl} className="flex w-full items-center justify-between gap-2" />
        </div>
      </ResponsiveDialog>

      <AlertDialog
        open={showCancelAppointmentDialog}
        onOpenChange={(nextOpen) => {
          setShowCancelAppointmentDialog(nextOpen);
          if (!nextOpen) {
            setPendingSubmitData(null);
            setAppointmentConfirmReason(null);
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("vaccines.form.cancelAppointmentDialog.title")}</AlertDialogTitle>
            <AlertDialogDescription>
              {appointmentConfirmReason === "disable_create_appointment" ? (
                <>
                  {t("vaccines.form.cancelAppointmentDialog.disableCreateAppointment")}
                </>
              ) : (
                <>
                  {t("vaccines.form.cancelAppointmentDialog.removeNextDoseDate")}
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => handleCancelAppointmentConfirm(false)}>
              {t("vaccines.form.cancelAppointmentDialog.keep")}
            </AlertDialogCancel>
            <AlertDialogAction onClick={() => handleCancelAppointmentConfirm(true)}>
              {t("vaccines.form.cancelAppointmentDialog.confirm")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("vaccines.history.deleteDialog.title")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("vaccines.history.deleteDialog.description")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteVaccination.isPending || updateVaccination.isPending}>
              {t("vaccines.history.deleteDialog.cancel")}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              disabled={deleteVaccination.isPending || updateVaccination.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {(deleteVaccination.isPending || updateVaccination.isPending)
                ? t("vaccines.history.deleteDialog.removing")
                : t("vaccines.history.deleteDialog.confirm")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {submissionMode === "immediate" && petSpeciesId && (
        <CreateApplicationDialog
          open={createVaccineDialogOpen}
          onOpenChange={setCreateVaccineDialogOpen}
          initialName={vaccineSearch}
          onApplicationCreated={(item) => {
            const option: VaccineOption = {
              kind: "item",
              value: `item:${item.id}`,
              label: item.name || "",
              rawName: item.name || "",
              itemId: item.id,
              applicationType: item.product_subtype?.name?.toLowerCase() === "medicamento" ? "medicamento" : "vacina",
              dose_intervals: [],
            };
            setCreatedOption(option);
            setVaccineSearch(item.name || "");
            setValue("item_id", item.id, { shouldDirty: true, shouldTouch: true, shouldValidate: true });
            const suggestedNextApplication = resolveDefaultNextApplicationOption(option) ?? option;
            setValue("next_application_item_id", suggestedNextApplication.itemId, { shouldDirty: true, shouldTouch: true, shouldValidate: true });
            nextDoseManualRef.current = false;
            applySuggestedNextDose(option, getValues("application_date"), true);
            // Auto-populate multi next-applications from catalog defaults
            const defaults = option.next_application_defaults;
            if (defaults && defaults.length > 0) {
              const appDate = getValues("application_date");
              setNextAppEntries(buildEntriesFromDefaults(defaults, appDate instanceof Date ? appDate : new Date()));
            }
            if (!vaccination) {
              setValue("dose_number", 1);
            }
            setCreateVaccineDialogOpen(false);
          }}
        />
      )}
    </>
  );
}

export const VaccinationFormDialog = ApplicationFormDialog;
